import { motion } from 'framer-motion';
import { Zap, Eye, Lock, Smartphone, Headphones } from 'lucide-react';
import SectionHead from './SectionHead';
import Reveal from './Reveal';
import { useLang } from '@/context/LanguageContext';

const SECURITY_IMG =
  'https://images.unsplash.com/photo-1576470135814-6f01266eff55?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHw0fHx5b3VuZyUyMGluZGlhbiUyMHByb2Zlc3Npb25hbCUyMHNtYXJ0cGhvbmUlMjBkaWdpdGFsJTIwYmFua2luZ3xlbnwwfHx8fDE3OTA4NjQ0MzV8MA&ixlib=rb-4.1.0&q=85';

const cardCls = 'h-full rounded-2xl border border-white/10 bg-panel/60 p-7 transition-colors hover:border-pulse/40';

export default function BentoFeatures() {
  const { t } = useLang();
  const [c1, c2, c3, c4, c5] = t.bento.cards;
  const tags = t.bento.tag;

  const Tag = ({ children }) => (
    <span className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-pulse">{children}</span>
  );

  return (
    <section id="features" className="py-24 lg:py-32" data-testid="bento-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead eyebrow={t.bento.eyebrow} title={t.bento.title} />

        <div className="mt-14 grid gap-5 lg:grid-cols-4">
          <Reveal className="lg:col-span-2 lg:row-span-2" delay={0}>
            <div data-testid="bento-card-approval" className={`${cardCls} relative flex flex-col overflow-hidden`}>
              <Tag>{tags[0]}</Tag>
              <h3 className="mt-3 flex items-center gap-3 font-display text-2xl font-bold text-white">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-pulse/10 text-pulse">
                  <Zap size={19} />
                </span>
                {c1.t}
              </h3>
              <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-400">{c1.d}</p>
              <div className="mt-auto flex flex-col justify-end gap-4 pt-8">
                {[
                  { label: '01 · Application', w: '100%' },
                  { label: '02 · Checks', w: '62%' },
                  { label: '03 · Approval', w: '24%' },
                ].map((row, i) => (
                  <div key={row.label}>
                    <div className="mb-1.5 flex justify-between font-mono text-[10px] uppercase tracking-widest text-slate-500">
                      <span>{row.label}</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-white/5">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: row.w }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.1, delay: 0.3 + i * 0.25, ease: [0.16, 1, 0.3, 1] }}
                        className={`h-full rounded-full ${i === 2 ? 'bg-gold' : 'bg-pulse'}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div data-testid="bento-card-pricing" className={cardCls}>
              <Tag>{tags[1]}</Tag>
              <h3 className="mt-3 flex items-center gap-3 font-display text-xl font-bold text-white">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-pulse/10 text-pulse">
                  <Eye size={19} />
                </span>
                {c2.t}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">{c2.d}</p>
              <div className="mt-5 space-y-2 rounded-xl bg-ink/60 p-4 font-mono text-xs text-slate-300">
                <p data-testid="bento-rate-daily">0.1% / day — daily interest</p>
                <p data-testid="bento-rate-fee">2.5% — one-time processing fee</p>
                <p data-testid="bento-rate-gst">18% — GST on fee only</p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div data-testid="bento-card-security" className={`${cardCls} flex gap-5 overflow-hidden p-0`}>
              <div className="flex-1 p-7">
                <Tag>{tags[2]}</Tag>
                <h3 className="mt-3 flex items-center gap-3 font-display text-xl font-bold text-white">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-pulse/10 text-pulse">
                    <Lock size={18} />
                  </span>
                  {c3.t}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{c3.d}</p>
              </div>
              <div className="relative hidden w-2/5 sm:block">
                <img
                  src={SECURITY_IMG}
                  alt="Secure digital KYC on mobile"
                  className="h-full w-full object-cover saturate-[0.75]"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-panel via-panel/30 to-transparent" />
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.3}>
            <div data-testid="bento-card-repayment" className={cardCls}>
              <Tag>{tags[3]}</Tag>
              <h3 className="mt-3 flex items-center gap-3 font-display text-xl font-bold text-white">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-pulse/10 text-pulse">
                  <Smartphone size={19} />
                </span>
                {c4.t}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">{c4.d}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {['UPI', 'NetBanking', 'Debit card', 'Autopay'].map((m) => (
                  <span
                    key={m}
                    className="rounded-full bg-white/5 px-3 py-1 font-mono text-[11px] font-semibold text-slate-300"
                  >
                    {m}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.4}>
            <div data-testid="bento-card-support" className={cardCls}>
              <Tag>{tags[4]}</Tag>
              <h3 className="mt-3 flex items-center gap-3 font-display text-xl font-bold text-white">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-pulse/10 text-pulse">
                  <Headphones size={19} />
                </span>
                {c5.t}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">{c5.d}</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
